import React, { useEffect, useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  ReferenceInput,
  SelectInput,
  NumberInput,
  required,
  useGetList,
  useCreate,
  useRedirect,
  useNotify,
  useDataProvider,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';
import RolePermissionManager from '../../components/RolePermissionManager';
import { useRolePermissionTransform } from '../../hooks/useRolePermissionTransform';

const DashboardsCreate = () => {
  // Hook para transformación de roles
  const { transformRoles } = useRolePermissionTransform();

  // Estado para almacenar el parámetro preseleccionado
  const [preselectedGroupId, setPreselectedGroupId] = useState<string | null>(
    null
  );
  const [preselectedGroupName, setPreselectedGroupName] = useState<string>('');
  const dataProvider = useDataProvider();
  const [loadingPreGroup, setLoadingPreGroup] = useState(false);

  // Estado para el filtro jerárquico
  const [hierarchicalFilter, setHierarchicalFilter] =
    useState<HierarchicalFilterSelections>({});

  // Obtener parámetros de la URL usando window.location de forma segura
  useEffect(() => {
    // Intentar primero con search (?query) y también con hash (#/ruta?query)
    let groupId: string | null = null;
    if (window.location.search) {
      const params = new URLSearchParams(window.location.search);
      groupId = params.get('dashboardGroupId');
    }
    if (!groupId) {
      const hash = window.location.hash;
      const queryIndex = hash.indexOf('?');
      if (queryIndex !== -1) {
        const queryString = hash.substring(queryIndex + 1);
        const urlParams = new URLSearchParams(queryString);
        groupId = urlParams.get('dashboardGroupId');
      }
    }
    setPreselectedGroupId(groupId);
  }, []);

  // Cargar datos del grupo preseleccionado manualmente
  useEffect(() => {
    const loadGroup = async () => {
      if (!preselectedGroupId) return;
      setLoadingPreGroup(true);
      try {
        const resp = await dataProvider.getOne('dashboardgroups', {
          id: Number(preselectedGroupId),
        });
        setPreselectedGroupName(resp.data?.groupName || '');
      } catch (e) {
        console.error('Error cargando grupo preseleccionado', e);
      } finally {
        setLoadingPreGroup(false);
      }
    };
    loadGroup();
  }, [preselectedGroupId, dataProvider]);

  console.log('PARAMS', preselectedGroupId);

  // Obtener lista de roles
  const { data: roles, isLoading: rolesLoading } = useGetList('roles', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  const roleChoices = roles
    ? roles.map((role: any) => ({
        id: role.name || role.roleName,
        name: role.name || role.roleName,
      }))
    : [];

  // Función para manejar cambios en el filtro jerárquico
  const handleHierarchicalFilterChange = (
    selections: HierarchicalFilterSelections
  ) => {
    setHierarchicalFilter(selections);
  };

  // Hooks para manejar la creación personalizada
  const [create] = useCreate();
  const redirect = useRedirect();
  const notify = useNotify();

  // Función personalizada para manejar el envío del formulario
  const handleSave = async (data: any) => {
    try {
      // Incluir dashboardGroupId si viene preseleccionado
      let payload: any = { ...data };
      if (preselectedGroupId) {
        payload.dashboardGroupId = parseInt(preselectedGroupId, 10);
      }
      // Transformar roles
      payload = transformRoles(payload);
      console.log('Payload original:', data);
      console.log('Payload transformado enviado:', payload);

      await create('dashboards', { data: payload });

      notify('Dashboard creado exitosamente', { type: 'success' });

      // Redirección personalizada
      const redirectTo = preselectedGroupId
        ? `/dashboardgroups/${preselectedGroupId}/show`
        : '/dashboards';
      redirect(redirectTo);
    } catch (error) {
      console.error('Error creando dashboard:', error);
      notify('Error al crear el dashboard', { type: 'error' });
    }
  };
  return (
    <Create
      title='Crear Dashboard'
      redirect={false} // Deshabilitamos el redirect automático
      transform={transformRoles}
    >
      <Box sx={{ padding: 3, maxWidth: 1000, margin: '0 auto' }}>
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardContent>
            <Box display='flex' alignItems='center' mb={3}>
              <DashboardIcon
                sx={{ mr: 2, fontSize: 32, color: 'primary.main' }}
              />
              <Box>
                {' '}
                <Typography variant='h5' component='h1' fontWeight='bold'>
                  Nuevo Dashboard
                </Typography>
                {preselectedGroupId && (
                  <Typography variant='body2' color='text.secondary'>
                    Se creará en el grupo seleccionado (grupo bloqueado)
                  </Typography>
                )}
              </Box>
            </Box>{' '}
            <SimpleForm
              onSubmit={handleSave} // Usar nuestro handler personalizado
              // Establecer valores por defecto si se pasa un dashboardGroupId
              defaultValues={
                preselectedGroupId
                  ? { dashboardGroupId: parseInt(preselectedGroupId) }
                  : undefined
              }
              sx={{
                '& .RaSimpleForm-toolbar': {
                  marginTop: 3,
                  padding: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 2,
                },
              }}
            >
              <Grid container spacing={3}>
                {/* Información Principal */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <DashboardIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Información del Dashboard
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <TextInput
                        source='dashboardName'
                        label='Nombre del Dashboard'
                        validate={[required()]}
                        fullWidth
                        sx={{ mb: 2 }}
                        helperText='Nombre descriptivo para el dashboard'
                      />

                      <NumberInput
                        source='position'
                        label='Posición'
                        fullWidth
                        sx={{ mb: 2 }}
                        helperText='Orden de visualización (opcional)'
                        min={0}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Grupo y Configuración */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <GroupIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Asignación y Permisos
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      {/* Filtro Jerárquico */}
                      {!preselectedGroupId && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant='subtitle2' gutterBottom>
                            Filtrar por Planta
                          </Typography>
                          <HierarchicalFilter
                            levels={['plant']}
                            onChange={handleHierarchicalFilterChange}
                            size='small'
                          />
                        </Box>
                      )}

                      {preselectedGroupId ? (
                        <Box>
                          <Typography variant='subtitle2' gutterBottom>
                            Grupo de Dashboard
                          </Typography>
                          <Typography variant='body1' color='text.primary'>
                            {preselectedGroupName ||
                              `ID: ${preselectedGroupId}`}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            Grupo bloqueado (seleccionado desde la URL)
                          </Typography>
                        </Box>
                      ) : hierarchicalFilter.plantId ? (
                        <ReferenceInput
                          source='dashboardGroupId'
                          reference='dashboardgroups'
                          sort={{ field: 'groupName', order: 'ASC' }}
                          filter={{ plantId: hierarchicalFilter.plantId }}
                        >
                          <SelectInput
                            optionText='groupName'
                            optionValue='id'
                            label='Grupo de Dashboard'
                            validate={[required()]}
                            fullWidth
                            helperText={`Grupos de dashboard de la planta: ${hierarchicalFilter.plantName}`}
                          />
                        </ReferenceInput>
                      ) : (
                        <Box>
                          <Typography variant='body2' color='text.secondary'>
                            Selecciona una planta para cargar los grupos
                            disponibles.
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Gestión de Roles */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <RolePermissionManager
                        title='Permisos del Dashboard'
                        subtitle='Configurar acceso y permisos para este dashboard'
                        helperText='Los usuarios con diferentes roles tendrán diferentes niveles de acceso a este dashboard y sus funcionalidades.'
                        showCard={false}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </SimpleForm>
          </CardContent>
        </Card>
      </Box>
    </Create>
  );
};

export default DashboardsCreate;
